import { useMemo, useState, useEffect, useRef } from 'react';
import LocatorResult from './LocatorResult';
import { type Station } from './Locator.types';

type LocatorResultsProps = {
  currentDuration: string;
  currentStation: string;
  stations: Station[];
  onCurrentStationClose: () => void;
  wrapperHeight: number;
};

const LocatorResults: React.FC<LocatorResultsProps> = ({
  currentDuration,
  currentStation,
  stations,
  onCurrentStationClose,
  wrapperHeight,
}) => {
  const currentStationData = useMemo(
    () => stations.find(({ uuid }) => uuid === currentStation),
    [currentStation],
  );

  const [isInitialised, setIsInitialised] = useState(false);
  const resultsEl = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const resultsElCurrent = resultsEl.current;

  useEffect(() => {
    if (isInitialised === false) {
      setIsInitialised(true);
    }

    if (resultsElCurrent && stations.length < 1) {
      resultsElCurrent.style.top = '0';
    }
  }, [stations]);

  // Allow the currently selected station to animate (mobile only).
  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;

      setIsMobile(width <= 992);
    });
    observer.observe(document.documentElement);

    let top = '100%';

    if (
      resultsElCurrent &&
      isMobile &&
      !resultsElCurrent.classList.contains('locator-results--no-results')
    ) {
      if (currentStation) {
        const listInnerHeight = resultsElCurrent.querySelector(
          '.locator-results__list',
        ).clientHeight;
        top = `${wrapperHeight - listInnerHeight}px`;
      }

      resultsElCurrent.style.top = top;
      resultsElCurrent.setAttribute('initial-top', top);
    }
  }, [currentStation, wrapperHeight]);

  return (
    <div
      className={`locator-results ${
        stations.length < 1 ? ' locator-results--no-results' : ''
      }`}
      ref={resultsEl}
    >
      <ul
        className={`locator-results__list${
          stations.length < 1 ? ' locator-results__list--no-results' : ''
        }`}
      >
        {stations.length > 0 && (
          <>
            {currentStation && (
              <LocatorResult
                station={currentStationData}
                currentDuration={currentDuration}
                isCurrentStation
                onClose={onCurrentStationClose}
              />
            )}

            <li className="locator-results__summary h6">
              {stations.length} {stations.length === 1 ? 'station' : 'stations'}{' '}
              found
            </li>
            {stations.map((station, i) => {
              // The current station is always rendered at the top of the list, so don't re-render it here.
              if (currentStation === station.site_id) {
                return null;
              }

              return (
                <LocatorResult
                  key={i}
                  station={station}
                  currentDuration={currentDuration}
                />
              );
            })}
          </>
        )}

        {isInitialised && stations.length < 1 && (
          <li>
            <span className="locator-results__empty h6">
              Sorry, no results found.
              <br />
              Please try again
            </span>
          </li>
        )}
      </ul>
    </div>
  );
};

export default LocatorResults;
